import { useState, useEffect } from "react";

// Types
export interface WeatherPeriod {
  temp: number;
  description: string;
  icon: string;
  label: string;
}

export interface WeatherData {
  current: WeatherPeriod;
  forecasts: WeatherPeriod[];
  city: string;
}

export interface CalendarEvent {
  summary: string;
  start: string;
  end: string;
  calendar: string;
  color: string;
  isAllDay: boolean;
}

export interface Todo {
  id: number;
  text: string;
  done: boolean;
  hidden_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BoardState {
  weather: WeatherData | null;
  events: Record<number, CalendarEvent[]>;
  note: string | null;
  todos: Todo[];
  lastUpdated: string;
  showQRCode: boolean;
  qrCodeRemainingSeconds: number;
  backgroundColor: string;
}

// Auth state
export interface AuthState {
  isLocalhost: boolean;
  authorized: boolean;
  deviceName: string | null;
  checked: boolean;
  headers: Record<string, string>;
}

// Initialize WebSocket connection once
let wsInitialized = false;
let wasDisconnected = false;
let reconnectTimeout = 0;
// WebSocket reconnection state - declared early so setDeviceToken can reset it
let wasUnauthorized = false;

// Global state and listeners
let globalState: BoardState = {
  weather: null,
  events: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
  note: null,
  todos: [],
  lastUpdated: "",
  showQRCode: false,
  qrCodeRemainingSeconds: 0,
  backgroundColor: "#f3f4f6", // default: gray-100
};

const listeners = new Set<(state: BoardState) => void>();

function updateState(newState: BoardState) {
  globalState = newState;
  listeners.forEach((listener) => listener(newState));
}

// Auth state management
let authState: AuthState = {
  isLocalhost: false,
  authorized: false,
  deviceName: null,
  checked: false,
};

const authListeners = new Set<(state: AuthState) => void>();

function updateAuthState(newState: AuthState) {
  authState = newState;
  authListeners.forEach((listener) => listener(newState));
}

// Device token management
export function getDeviceToken(): string | null {
  return localStorage.getItem("deviceToken");
}

export function setDeviceToken(token: string): void {
  localStorage.setItem("deviceToken", token);
  // Reset unauthorized flag so reconnection can be attempted with new token
  wasUnauthorized = false;
}

export function clearDeviceToken(): void {
  localStorage.removeItem("deviceToken");
}

// Check auth status
let authCheckStarted = false;

async function checkAuthStatus(): Promise<AuthState> {
  if (authCheckStarted && authState.checked) {
    return authState;
  }
  authCheckStarted = true;

  const token = getDeviceToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await fetch("/api/auth/status", { headers });
    const data = await res.json();
    const newAuthState: AuthState = {
      isLocalhost: data.isLocalhost,
      authorized: data.authorized,
      deviceName: data.deviceName,
      headers,
      checked: true,
    };
    updateAuthState(newAuthState);
    return newAuthState;
  } catch (error) {
    console.error("Failed to check auth status:", error);
    const newAuthState: AuthState = {
      isLocalhost: false,
      authorized: false,
      deviceName: null,
      checked: true,
    };
    updateAuthState(newAuthState);
    return newAuthState;
  }
}

function initWebSocket() {
  if (wsInitialized) return;
  wsInitialized = true;

  // Build headers with auth token if available
  const token = getDeviceToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Fetch initial state
  fetch("/api/state", { headers })
    .then((res) => {
      if (res.status === 401) {
        // Unauthorized - trigger auth check
        checkAuthStatus();
        return null;
      }
      return res.json();
    })
    .then((data) => {
      if (data) updateState(data);
    })
    .catch((err) => console.error("Failed to fetch initial state:", err));

  // Connect WebSocket with token
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  let wsUrl = `${protocol}//${window.location.host}/ws`;
  if (token) {
    wsUrl += `?token=${encodeURIComponent(token)}`;
  }
  const ws = new WebSocket(wsUrl);

  ws.onopen = () => {
    console.log("WebSocket connected");
    // Full page reload if reconnecting after disconnect (container restart)
    if (wasDisconnected) {
      console.log("Reconnected after disconnect, reloading page...");
      window.location.reload();
      return;
    }
  };

  ws.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);
      if (message.type === "unauthorized") {
        // Handle unauthorized - update auth state and prevent reconnection
        wasUnauthorized = true;
        updateAuthState({
          isLocalhost: false,
          authorized: false,
          deviceName: null,
          checked: true,
          headers: {},
        });
        return;
      }
      if (message.type === "state") {
        updateState(message.data);
      }
    } catch (error) {
      console.error("Failed to parse WebSocket message:", error);
    }
  };

  ws.onclose = () => {
    wsInitialized = false;
    wasDisconnected = true;
    window.clearTimeout(reconnectTimeout);

    // Only reconnect if we weren't explicitly unauthorized by the server
    if (wasUnauthorized) {
      console.log("WebSocket disconnected (unauthorized), not reconnecting");
      return;
    }

    console.log("WebSocket disconnected, reconnecting in 3s...");
    reconnectTimeout = window.setTimeout(initWebSocket, 3000);
  };

  ws.onerror = (error) => {
    console.error("WebSocket error:", error);
  };
}

// Hook to use the board state
export function useBoardState(): BoardState {
  const [state, setState] = useState(globalState);

  useEffect(() => {
    initWebSocket();
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

// Convenience hooks for specific parts of state
export function useWeather(): WeatherData | null {
  const state = useBoardState();
  return state.weather;
}

export function useEvents(): Record<number, CalendarEvent[]> {
  const state = useBoardState();
  return state.events;
}

export function useNote(): string | null {
  const state = useBoardState();
  return state.note;
}

export function useTodos(): Todo[] {
  const state = useBoardState();
  return state.todos;
}

export async function toggleTodo(
  id: number,
  headers: Record<string, string>
): Promise<Todo | null> {
  try {
    const res = await fetch(`/api/todos/${id}/toggle`, {
      method: "POST",
      headers,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (error) {
    console.error("Failed to toggle todo:", error);
    return null;
  }
}

// Auth state hook
export function useAuthState(): AuthState {
  const [state, setState] = useState(authState);

  useEffect(() => {
    authListeners.add(setState);
    checkAuthStatus();
    return () => {
      authListeners.delete(setState);
    };
  }, []);

  return state;
}

// QR code visibility hook
export function useQRCode(): { visible: boolean; remainingSeconds: number } {
  const state = useBoardState();
  return {
    visible: state.showQRCode,
    remainingSeconds: state.qrCodeRemainingSeconds,
  };
}

// Background color hook
export function useBackgroundColor(): string {
  const state = useBoardState();
  return state.backgroundColor;
}
